import { useNavigate } from 'react-router-dom';
import type { LeadStatus } from '@neuroedge/shared';
import { Button, Card, CollapsibleSection, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useLead, useDeleteLead, useUpdateLead } from '../../api/leads';
import { useSetLeadTags } from '../../api/tags';
import { useSetLeadCustomFields } from '../../api/custom-fields';
import { TaskList } from '../tasks/TaskList';
import { NoteList } from '../notes/NoteList';
import { TagPicker } from '../tags/TagPicker';
import { FollowUpList } from '../follow-ups/FollowUpList';
import { ActivityTimeline } from '../activities/ActivityTimeline';
import { CustomFieldValues } from '../custom-fields/CustomFieldValues';
import { FlowAnswersList } from '../flow-answers/FlowAnswersList';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'HOLD', label: 'Hold' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
  { value: 'ABANDONED', label: 'Abandoned' },
];

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: 'var(--color-text-muted)' }}>{label}</dt>
      <dd style={{ margin: 0 }}>{value || '—'}</dd>
    </>
  );
}

/**
 * The actual lead detail content - core fields plus a set of collapsible
 * sections (tags, custom fields, tasks, follow-ups, notes, activity) so a
 * quick look doesn't force everything open at once. Shared between the
 * standalone `/leads/:id` page (`LeadDetailPage`) and the board's quick
 * popup (`LeadDetailModal`) - `onClose`, when given, is called after a
 * successful delete instead of navigating to `/leads` (the modal is
 * already sitting on that page).
 */
export function LeadDetailContent({ id, onClose }: { id: string; onClose?: () => void }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const { data: lead, isPending, isError, error, refetch } = useLead(id);
  const del = useDeleteLead();
  const updateStatus = useUpdateLead(id);
  const setTags = useSetLeadTags(id);
  const setCustomFields = useSetLeadCustomFields(id);
  const canUpdate = hasPermission('leads.update');

  function onStatusChange(status: LeadStatus) {
    updateStatus.mutate(
      { status },
      {
        onSuccess: () => toast.success('Lead status updated.'),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not update status.'),
      },
    );
  }

  if (isPending) return <PageState kind="loading" message="Loading lead…" />;
  if (isError) {
    return error.code === 'RESOURCE_NOT_FOUND' ? (
      <PageState kind="empty" title="Lead not found" message="It may have been deleted." />
    ) : (
      <PageState kind="error" message={error.message} onRetry={() => void refetch()} />
    );
  }

  async function onDelete() {
    if (!lead) return;
    if (!window.confirm(`Delete "${lead.title}"?`)) return;
    try {
      await del.mutateAsync(lead.id);
      toast.success('Lead deleted.');
      if (onClose) onClose();
      else navigate('/leads', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the lead.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{lead.title}</h1>
        <span style={{ flex: 1 }} />
        {hasPermission('leads.update') ? (
          <Button variant="secondary" onClick={() => navigate(`/leads/${lead.id}/edit`)}>
            Edit
          </Button>
        ) : null}
        {hasPermission('leads.delete') ? (
          <Button variant="secondary" onClick={() => void onDelete()} disabled={del.isPending}>
            Delete
          </Button>
        ) : null}
      </div>

      <Card>
        <dl
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'var(--space-2) var(--space-4)',
          }}
        >
          <Row
            label="Status"
            value={
              canUpdate ? (
                <select
                  aria-label="Lead status"
                  className="ne-input"
                  style={{ fontSize: 'var(--font-size-sm)' }}
                  value={lead.status}
                  disabled={updateStatus.isPending}
                  onChange={(e) => onStatusChange(e.target.value as LeadStatus)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ne-badge">{lead.status}</span>
              )
            }
          />
          <Row label="Pipeline" value={lead.pipeline.name} />
          <Row label="Stage" value={lead.stage.name} />
          <Row
            label="Contact"
            value={
              <>
                {lead.contact.displayName}
                {lead.contact.email ? ` · ${lead.contact.email}` : ''}
                {lead.contact.phone ? (
                  <>
                    {' · '}
                    <a
                      href={`tel:${lead.contact.phone}`}
                      style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
                    >
                      📞 {lead.contact.phone}
                    </a>
                  </>
                ) : null}
              </>
            }
          />
          <Row
            label="Value"
            value={lead.valueAmount != null ? `${lead.currency} ${lead.valueAmount}` : null}
          />
          <Row label="Source" value={lead.source} />
          <Row
            label="Assigned to"
            value={
              canUpdate ? (
                <AssigneeSelect
                  compact
                  label="Assigned to"
                  value={lead.assignedUser?.tenantUserId ?? ''}
                  currentName={lead.assignedUser?.displayName}
                  disabled={updateStatus.isPending}
                  onChange={(next) =>
                    updateStatus.mutate(
                      { assignedUserId: next || null },
                      {
                        onSuccess: () =>
                          toast.success(next ? 'Lead assigned.' : 'Lead unassigned.'),
                        onError: (err) =>
                          toast.error(
                            err instanceof Error ? err.message : 'Could not change the assignee.',
                          ),
                      },
                    )
                  }
                />
              ) : lead.assignedUser ? (
                lead.assignedUser.displayName
              ) : (
                'Unassigned'
              )
            }
          />
          <Row
            label="Expected close"
            value={
              lead.expectedCloseAt ? new Date(lead.expectedCloseAt).toLocaleDateString() : null
            }
          />
          <Row label="Created" value={new Date(lead.createdAt).toLocaleString()} />
          <Row label="Updated" value={new Date(lead.updatedAt).toLocaleString()} />
        </dl>
      </Card>

      {Array.isArray(lead.metadata?.answers) && lead.metadata.answers.length > 0 ? (
        <CollapsibleSection title="Captured from WhatsApp conversation" defaultOpen>
          <FlowAnswersList metadata={lead.metadata} />
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection title="Tags">
        <TagPicker title="" tags={lead.tags} onSave={(tagIds) => setTags.mutateAsync({ tagIds })} />
      </CollapsibleSection>

      <CollapsibleSection title="Custom fields">
        <CustomFieldValues
          entityType="LEAD"
          values={lead.customFields}
          onSave={(values) => setCustomFields.mutateAsync({ values })}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Tasks">
        <TaskList title="" query={{ leadId: lead.id }} attachTo={{ leadId: lead.id }} />
      </CollapsibleSection>

      <CollapsibleSection title="Follow-ups">
        <FollowUpList title="" query={{ leadId: lead.id }} attachTo={{ leadId: lead.id }} />
      </CollapsibleSection>

      <CollapsibleSection title="Notes">
        <NoteList title="" query={{ leadId: lead.id }} attachTo={{ leadId: lead.id }} />
      </CollapsibleSection>

      <CollapsibleSection title="Activity">
        <ActivityTimeline title="" query={{ leadId: lead.id }} attachTo={{ leadId: lead.id }} />
      </CollapsibleSection>
    </div>
  );
}
